import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from '@themesberg/react-bootstrap';
import PageHeader, { AlertMessage } from '../../components/PageHeader';
import { blocklistApi } from '../../services/api';

export default function Blocklist() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    subjectType: 'email',
    subjectValue: '',
    reason: '',
    expiresAt: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await blocklistApi.list();
      setEntries(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await blocklistApi.create({
        subjectType: form.subjectType,
        subjectValue: form.subjectValue,
        reason: form.reason || null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      });
      setForm({ subjectType: 'email', subjectValue: '', reason: '', expiresAt: '' });
      setSuccess('Entry added');
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this blocklist entry?')) return;
    setError('');
    try {
      await blocklistApi.delete(id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <PageHeader title="Blocklist" subtitle="Explicit deny list checked before velocity rules on every /decide call." />

      <AlertMessage variant="danger" message={error} onClose={() => setError('')} />
      <AlertMessage variant="success" message={success} onClose={() => setSuccess('')} />

      <Row>
        <Col lg={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body>
              <h5 className="mb-3">Add entry</h5>
              <Form onSubmit={handleCreate}>
                <Form.Group className="mb-3">
                  <Form.Label>Subject type</Form.Label>
                  <Form.Select value={form.subjectType} onChange={(e) => setForm({ ...form, subjectType: e.target.value })}>
                    <option value="email">email</option>
                    <option value="phone">phone</option>
                    <option value="ip">ip</option>
                    <option value="deviceId">deviceId</option>
                    <option value="userId">userId</option>
                    <option value="sessionId">sessionId</option>
                    <option value="customerId">customerId</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Subject value</Form.Label>
                  <Form.Control required value={form.subjectValue} onChange={(e) => setForm({ ...form, subjectValue: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-4">
                  <Form.Label>Expires at (optional)</Form.Label>
                  <Form.Control type="datetime-local" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
                </Form.Group>
                <Button variant="primary" type="submit" className="w-100">Add to blocklist</Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={8} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Body className="p-0">
              {loading ? (
                <p className="p-4 mb-0">Loading…</p>
              ) : entries.length === 0 ? (
                <p className="p-4 mb-0 text-gray">No blocklist entries.</p>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="thead-light">
                    <tr>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Reason</th>
                      <th>Expires</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr key={entry.id}>
                        <td><code>{entry.subjectType}</code></td>
                        <td>{entry.subjectValue}</td>
                        <td>{entry.reason || '—'}</td>
                        <td>{entry.expiresAt ? new Date(entry.expiresAt).toLocaleString() : 'Never'}</td>
                        <td className="text-end">
                          <Button size="sm" variant="outline-danger" onClick={() => handleDelete(entry.id)}>Delete</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
